import { useState, useCallback } from 'react';
import axios from 'axios';
import { enhancedApi } from '../../../shared/services/enhancedApi';

interface PlanOption {
  productId: number;
  productName: string;
  productLabel: string;
  maxBenefitLimit: number;
  planType: string;
  rate: {
    rateAmount: number;
    currency: {
      currencyId: number;
      currencyCode: string;
      symbol: string;
    };
  };
  benefitsCategories: Array<{
    benefitsCategory: string;
    categoryCoverage: string;
    benefits: Array<{
      benefitName: string;
      benefitCoverage: string;
    }>;
  }>;
  exclusion: string[];
  productOverview: Array<{
    productOverviewId: number;
    productId: number;
    description: string;
  }>;
}

interface ApiResponse {
  success: boolean;
  code: number;
  plan: Array<{
    product: PlanOption;
  }>;
  message?: string;
  planUpgradeMsg?: string;
}

interface SingleTripParams {
  planType: string; // 'Individual', 'Family', 'Group'
  startTrip: string;
  destination: string[]; // Array of destination names
  endTrip: string;
  startDate: string;
  endDate: string;
  dsName?: string; // Default to 'domestic'
  transport?: 'land' | 'sea' | 'air'; // Transport mode for domestic category (includes ShortSecure modes)
}

interface MultiTripParams {
  dsName?: string; // Default to 'domestic' - planType not needed for multi-trip API
}


interface UsePlanRatesReturn {
  planOptions: PlanOption[];
  loading: boolean;
  error: string | null;
  fetchSingleTripRates: (params: SingleTripParams, provinceMapping?: Record<string, string>, destinationMapping?: Record<string, string>) => Promise<void>;
  fetchMultiTripRates: (params: MultiTripParams) => Promise<void>;
  fetchMultiTripInternationalRates: () => Promise<void>;
  clearPlans: () => void;
  validatePlanTypeForDsName: (planType: string, dsName: string) => { isValid: boolean; message?: string };
}

export const usePlanRates = (): UsePlanRatesReturn => {
  const [planOptions, setPlanOptions] = useState<PlanOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Map plan type to API number
  const getPlanTypeNumber = (planType: string): number => {
    switch (planType) {
      case 'Individual':
        return 1;
      case 'Family':
        return 2;
      case 'Group':
        return 3;
      default:
        return 1; // Default to Individual
    }
  };

  // Validate plan type for specific ds_name
  const validatePlanTypeForDsName = useCallback((planType: string, dsName: string): { isValid: boolean; message?: string } => {
    const normalizedDsName = dsName.toLowerCase().trim();
    
    // Short Secure only supports Individual and Group
    if (normalizedDsName === 'short_secure' || normalizedDsName === 'shortsecure' || normalizedDsName === 'short secure') {
      if (planType === 'Family') {
        return {
          isValid: false,
          message: 'Short Secure plan type only supports Individual and Group plans, not Family plans.'
        };
      }
    }
    
    return { isValid: true };
  }, []);

  // Map ds_name to API format
  const mapDsName = (dsName: string): string => {
    console.log('mapDsName - input dsName:', dsName);
    
    const normalizedDsName = dsName.toLowerCase().trim();
    
    let mappedValue: string;
    switch (normalizedDsName) {
      case 'domestic':
        mappedValue = 'domestic';
        break;
      case 'asia_oceania':
      case 'asia and oceania':
        mappedValue = 'asia_oceania';
        break;
      case 'world_wide_elite':
      case 'worldwideelite':
      case 'worldwide elite':
      case 'worldwide_elite':        
        mappedValue = 'world_wide_elite';
        break;
      case 'short_secure':
      case 'shortsecure':
      case 'short secure':
        mappedValue = 'short_secure';
        break;
      default:
        console.warn(`Unknown dsName: ${dsName}, defaulting to domestic`);
        mappedValue = 'domestic';
    }
    
    console.log('mapDsName - output mappedValue:', mappedValue);
    return mappedValue;
  };

  // Check if ds_name requires transport parameter
  const requiresTransport = (dsName: string): boolean => {
    const normalizedDsName = dsName.toLowerCase().trim();
    return normalizedDsName === 'short_secure' || 
           normalizedDsName === 'shortsecure' || 
           normalizedDsName === 'short secure';
  };

  const fetchSingleTripRates = useCallback(async (
    params: SingleTripParams,
    _provinceMapping?: Record<string, string>, // eslint-disable-line @typescript-eslint/no-unused-vars
    _destinationMapping?: Record<string, string> // eslint-disable-line @typescript-eslint/no-unused-vars
  ) => {
    setLoading(true);
    setError(null);

    try {
      // Validate plan type for the given ds_name
      const validation = validatePlanTypeForDsName(params.planType, params.dsName || 'domestic');
      if (!validation.isValid) {
        throw new Error(validation.message);
      }

      const planTypeNumber = getPlanTypeNumber(params.planType);
      
      // Use destination names directly instead of mapping to codes
      const startTripName = params.startTrip;
      const endTripName = params.endTrip;
      
      // Join destination names with comma (no code mapping)
      const destinationNames = params.destination.join(',');

      // Map dsName properly
      const mappedDsName = mapDsName(params.dsName || 'domestic');

      // Build request parameters using destination names
      const requestParams: Record<string, string | number> = {
        planType: planTypeNumber,
        startTrip: startTripName,
        destination: destinationNames,
        endTrip: endTripName,
        startDate: params.startDate,
        endDate: params.endDate,
        ds_name: mappedDsName
      };

      // Add transport parameter for short_secure
      if (requiresTransport(mappedDsName)) {
        if (!params.transport) {
          throw new Error('Transport parameter (land or sea) is required for Short Secure plan type');
        }
        requestParams.transport = params.transport;
        console.log(`Adding transport parameter: ${params.transport} for short_secure plan`);
      }

      console.log('Fetching single trip plan rates with params:', requestParams);

      // Use enhanced API with caching and queuing
      const response = await enhancedApi.cachedGet<ApiResponse>(
        '/api/rates/single',
        { params: requestParams },
        5 * 60 * 1000 // 5 minutes TTL for plan rates (more dynamic)
      );

      const data: ApiResponse = response;

      if (data.success && data.plan) {
        const plans = data.plan.map(item => item.product);
        setPlanOptions(plans);
        console.log('Successfully fetched single trip plans:', plans);
        console.log(`Fetched ${plans.length} plans for dsName: ${mappedDsName}${requestParams.transport ? ` with transport: ${requestParams.transport}` : ''}`);
        
        // Log any upgrade messages
        if (data.planUpgradeMsg) {
          console.log('Plan upgrade message:', data.planUpgradeMsg);
        }
      } else {
        throw new Error(data.message || 'Failed to fetch plans');
      }
    } catch (err) {
      console.error('Error fetching single trip plan rates:', err);
      handleError(err);
    } finally {
      setLoading(false);
    }
  }, [validatePlanTypeForDsName]);

  const fetchMultiTripRates = useCallback(async (params: MultiTripParams) => {
    setLoading(true);
    setError(null);

    try {
      // Map dsName properly
      const mappedDsName = mapDsName(params.dsName || 'domestic');
      
      // Check if this is short_secure - multi-trip might not be supported
      if (requiresTransport(mappedDsName)) {
        console.warn('Short Secure plan type detected for multi-trip - this may not be supported');
      }
      
      // Build request parameters for multi-trip - ONLY ds_name is needed!
      const requestParams = {
        ds_name: mappedDsName
      };

      console.log('Fetching multi-trip plan rates with params:', requestParams);

      // Use enhanced API with caching and queuing
      const response = await enhancedApi.cachedGet<ApiResponse>(
        '/api/rates/multi',
        { params: requestParams },
        5 * 60 * 1000 // 5 minutes TTL for plan rates (more dynamic)
      );

      const data: ApiResponse = response;

      if (data.success && data.plan) {
        const plans = data.plan.map(item => item.product);
        setPlanOptions(plans);
        console.log('Successfully fetched multi-trip plans:', plans);
        console.log(`Fetched ${plans.length} multi-trip plans for dsName: ${mappedDsName} (no planType needed for multi-trip)`);
        
        // Log any upgrade messages
        if (data.planUpgradeMsg) {
          console.log('Plan upgrade message:', data.planUpgradeMsg);
        }
      } else {
        throw new Error(data.message || 'Failed to fetch multi-trip plans');
      }
    } catch (err) {
      console.error('Error fetching multi-trip plan rates:', err);
      handleError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchMultiTripInternationalRates = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      console.log('Fetching multi-trip international plan rates for both Asia Oceania and World Wide Elite');

      // Fetch both Asia Oceania and World Wide Elite plans simultaneously
      const [asiaResponse, worldwideResponse] = await Promise.all([
        enhancedApi.cachedGet<ApiResponse>(
          '/api/rates/multi',
          { params: { ds_name: 'asia_oceania' } },
          5 * 60 * 1000 // 5 minutes TTL for plan rates (more dynamic)
        ),
        enhancedApi.cachedGet<ApiResponse>(
          '/api/rates/multi',
          { params: { ds_name: 'world_wide_elite' } },
          5 * 60 * 1000 // 5 minutes TTL for plan rates (more dynamic)
        )
      ]);

      const allPlans: PlanOption[] = [];

      // Process Asia Oceania plans
      if (asiaResponse.success && asiaResponse.plan) {
        const asiaPlans = asiaResponse.plan.map(item => item.product);
        allPlans.push(...asiaPlans);
        console.log(`Successfully fetched ${asiaPlans.length} Asia Oceania multi-trip plans`);
        
        // Log any upgrade messages for Asia Oceania
        if (asiaResponse.planUpgradeMsg) {
          console.log('Asia Oceania plan upgrade message:', asiaResponse.planUpgradeMsg);
        }
      }

      // Process World Wide Elite plans
      if (worldwideResponse.success && worldwideResponse.plan) {
        const worldwidePlans = worldwideResponse.plan.map(item => item.product);
        allPlans.push(...worldwidePlans);
        console.log(`Successfully fetched ${worldwidePlans.length} World Wide Elite multi-trip plans`);
        
        // Log any upgrade messages for World Wide Elite
        if (worldwideResponse.planUpgradeMsg) {
          console.log('World Wide Elite plan upgrade message:', worldwideResponse.planUpgradeMsg);
        }
      }

      // Set all plans together
      setPlanOptions(allPlans);
      console.log(`Total fetched multi-trip international plans: ${allPlans.length}`);

      // Handle cases where no plans were returned
      if (allPlans.length === 0) {
        console.warn('No multi-trip international plans were returned from either Asia Oceania or World Wide Elite');
      }

    } catch (err) {
      console.error('Error fetching multi-trip international plan rates:', err);
      handleError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleError = (err: unknown) => {
    if (axios.isAxiosError(err)) {
      if (err.response) {
        // Server responded with error status
        setError(`Server error: ${err.response.status} - ${err.response.data?.message || err.message}`);
      } else if (err.request) {
        // Request was made but no response received
        setError('Network error: Unable to connect to server');
      } else {
        // Something else happened
        setError(`Request error: ${err.message}`);
      }
    } else {
      setError(err instanceof Error ? err.message : 'Failed to fetch plans');
    }
    
    setPlanOptions([]);
  };

  const clearPlans = useCallback(() => {
    setPlanOptions([]);
    setError(null);
    setLoading(false);
  }, []);

  return {
    planOptions,
    loading,
    error,
    fetchSingleTripRates,
    fetchMultiTripRates,
    fetchMultiTripInternationalRates,
    clearPlans,
    validatePlanTypeForDsName
  };
};
